import { html, nothing, render, TemplateResult } from 'lit';
import { ErrorCodes, Events, Property } from '../types';
import { FindingClassType } from '../../types';
import { ListItem } from '../../../ui/gzSingleSelectList/types';
import { EasmBulkChangePropertyDataProviderFactory } from '../factory/EasmBulkChangePropertyDataProviderFactory';
import { EasmBulkChangePropertyBaseDataProvider } from '../dataProvider/EasmBulkChangePropertyBaseDataProvider';
import { FontSizes, LabelWidthValues } from '../../../types/common/types';
import { translateFn } from '../../../utils/translator.helper';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { ButtonColors, ButtonTypes } from '../../../ui/gzButton/types';
import { GzCheckboxWebComponent } from '../../../ui/gzCheckbox';
import { GzSingleSelectWebComponent } from '../../../ui/gzSingleSelectDropdown';
import { GzTextareaWebComponent } from '../../../ui/gzInput';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { ToastPositionX, ToastPositionY, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import { GzInputErrorWebComponent } from '../../../ui/gzForm/inputError';
import { FormEvent } from '../../../ui/gzForm/formField/types/form-event.enum';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

export class EasmBulkChangePropertyWebComponent {
  private readonly dataProvider: EasmBulkChangePropertyBaseDataProvider;
  private dialog: GzDialogWebComponent;
  private confirmButton: GzButtonWebComponent;

  /**
   * @throws {Error}
   */
  public constructor(
    property: Property,
    private readonly findingsClassType: FindingClassType,
    private readonly findingIds: string[],
    private readonly preSelectedValue: any = null,
    private readonly preSelectedValueLabel: string | null = null,
    private readonly options: ListItem[] | null = null,
    private readonly selectedOptionValue: number | string | null = null
  ) {
    if (null !== this.preSelectedValue) {
      if (null === this.preSelectedValueLabel) {
        throw new Error('preSelectedValueLabel property is required when preSelectedValue is provided');
      }
    } else if (null === this.options) {
      throw new Error('Either one of preSelectedValue and options properties must be provided');
    }

    this.dataProvider = (new EasmBulkChangePropertyDataProviderFactory()).build<EasmBulkChangePropertyBaseDataProvider>(
      property
    );
  }

  public render(): void {
    this.dialog = GzDialogWebComponent.buildDialogElement({
      headline: this.dataProvider.getTitle(),
      bodyContent: this.renderDialogBody(),
      footerContent: this.renderDialogFooter(),
      isOpen: true,
    });
  }

  private renderDialogBody(): HTMLDivElement {
    const body = document.createElement('div') as HTMLDivElement;

    render(
      html`
        ${this.renderDescription()}

        ${this.renderOptions()}

        ${this.renderApplyToArtifactsOption()}

        ${this.renderNoteInput()}
      `,
      body
    );

    return body;
  }

  private renderDescription(): TemplateResult {
    return html`
      <div>
        <gz-text class="pb-3" fontSize="${FontSizes.HEADING_6}">
          ${unsafeHTML(this.dataProvider.getDescription(this.findingsClassType, this.preSelectedValueLabel))}
        </gz-text>
      </div>
    `;
  }

  private renderOptions(): TemplateResult | typeof nothing {
    if (null !== this.preSelectedValue) {
      return nothing;
    }

    const singleSelect = new GzSingleSelectWebComponent(), optionsFormField = new GzFormFieldWebComponent();

    singleSelect.id = 'easm-bulk-change-property-options';
    singleSelect.className = 'd-block pb-2';
    singleSelect.labelWidth = LabelWidthValues.FULL;
    singleSelect.placeholder = this.dataProvider.getOptionsPlaceholder();
    singleSelect.filterOptions = this.options as ListItem[];
    singleSelect.cannotDelete = true;

    if (null !== this.selectedOptionValue) {
      singleSelect.value = this.selectedOptionValue;
    }

    singleSelect.setAttribute('gz-form-input', '');

    optionsFormField.appendChild(singleSelect);
    optionsFormField.appendChild(new GzInputErrorWebComponent());

    optionsFormField.label = this.dataProvider.getOptionsLabel();
    optionsFormField.required = true;
    optionsFormField.Validators = [RequiredValidator(this.dataProvider.getRequiredOptionErrorMessage())];

    optionsFormField.addEventListener(FormEvent.FormValid, (): void => {
      this.confirmButton.disabled = false;
    });
    optionsFormField.addEventListener(FormEvent.FormInvalid, (): void => {
      this.confirmButton.disabled = true;
    });

    return html`
      <div>
        ${optionsFormField}
      </div>
    `;
  }

  private renderApplyToArtifactsOption(): TemplateResult | typeof nothing {
    if (FindingClassType.ARTIFACT === this.findingsClassType) {
      return nothing;
    }

    return html`
      <div>
        <gz-form-field>
          <gz-checkbox
            id="easm-bulk-change-property-apply-to-artifacts"
            class="pb-2"
            label="${this.dataProvider.getApplyToArtifactsOptionLabel()}"
            gz-form-input
          >
          </gz-checkbox>

          <gz-input-error></gz-input-error>
        </gz-form-field>
      </div>
    `;
  }

  private renderNoteInput(): TemplateResult {
    return html`
      <div>
        <gz-form-field class="label-mw-250" label="">
          <gz-textarea
            id="easm-bulk-change-property-note"
            hasCounter="true"
            maxChars="2048"
            placeholder="${translateFn('Notes')}"
            gz-form-input
          ></gz-textarea>

          <gz-input-error></gz-input-error>
        </gz-form-field>
      </div>
    `;
  }

  private renderDialogFooter(): HTMLDivElement {
    this.confirmButton = new GzButtonWebComponent();

    this.confirmButton.innerText = translateFn('Change');
    this.confirmButton.disabled = null === this.preSelectedValue && null === this.selectedOptionValue;
    this.confirmButton.addEventListener('click', this.changeBulkProperty.bind(this));

    const cancelButton = new GzButtonWebComponent(), footer = document.createElement('div') as HTMLDivElement;

    cancelButton.innerText = translateFn('Cancel');
    cancelButton.type = ButtonTypes.OUTLINE;
    cancelButton.color = ButtonColors.SECONDARY;
    cancelButton.addEventListener('click', (): void => this.dialog.close());

    render(
      html`
        ${this.confirmButton}
        ${cancelButton}
      `,
      footer
    );

    return footer;
  }

  private async changeBulkProperty(): Promise<void> {
    if (this.confirmButton.showSpinner) {
      return;
    }

    this.confirmButton.showSpinner = true;

    const value = null !== this.preSelectedValue
        ? this.preSelectedValue
        : (
          this.dialog.shadowRoot?.querySelector('#easm-bulk-change-property-options') as GzSingleSelectWebComponent
        ).value,
      updateRelatedArtifacts = FindingClassType.ASSET === this.findingsClassType
        ? (
          this.dialog.shadowRoot?.querySelector('#easm-bulk-change-property-apply-to-artifacts') as GzCheckboxWebComponent
        ).getFormValue()
        : false,
      note = (
        this.dialog.shadowRoot?.querySelector('#easm-bulk-change-property-note') as GzTextareaWebComponent
      ).getFormValue();

    try {
      const response = await this.dataProvider.changeBulkProperty(this.findingIds, value, updateRelatedArtifacts, note);

      this.showToast(this.dataProvider.getSuccessToastMessage(response), ToastSeverity.SUCCESS);

      document.body.dispatchEvent(new CustomEvent(Events.PROPERTY_CHANGED));
    } catch (error: any) {
      const errorMessage = ErrorCodes.TOO_MANY_FINDINGS_PROVIDED_FOR_UPDATE === error?.err?.code
        ? translateFn('You have reached the selection limit.')
        : this.dataProvider.getFailureToastMessage(error, value);

      this.showToast(errorMessage, ToastSeverity.ERROR);
    }

    this.dialog.close();
  }

  private showToast(message: string, severity: ToastSeverity): void {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: message,
      severity: severity,
      x: ToastPositionX.RIGHT,
      y: ToastPositionY.BOTTOM,
      autoCloseDelayMs: 5000,
    });
  }
}
